/**
 * The hero: the vessel turning in the dark, with embers going up past it.
 *
 * The object is a beacon, so the scene is a fire seen at night — the printed
 * shell lit by one warm key, with the two signal colours as rim lights either
 * side so the thing the page is about is present in the light itself rather
 * than painted on. Embers rise the way they do off a signal fire.
 *
 * What this deliberately does not do is fake the lamps. The real ones are two
 * LEDs under two toy bins, and they are photographed further down the page; a
 * pair of glowing spheres here would be an invention standing where evidence
 * belongs.
 *
 * Costs: one 1MB mesh, one 64-sprite ember pool, no shadows, no environment
 * map. The harness pauses it when it leaves the viewport.
 */

import { asset, type SceneDefinition } from '@remidus/web-core'

const MODEL = 'models/cad/CustomEnclosureTopV2.glb'
const EMBERS_DESKTOP = 64
const EMBERS_MOBILE = 28

/** One soft ember, drawn once into a canvas and shared by every sprite. */
function emberTexture(THREE: typeof import('three')) {
  const size = 64
  const canvas = document.createElement('canvas')
  canvas.width = canvas.height = size
  const ctx = canvas.getContext('2d')!
  const c = size / 2
  const glow = ctx.createRadialGradient(c, c, 0, c, c, c)
  glow.addColorStop(0, 'rgba(255,240,208,1)')
  glow.addColorStop(0.25, 'rgba(255,166,64,0.9)')
  glow.addColorStop(1, 'rgba(255,122,47,0)')
  ctx.fillStyle = glow
  ctx.fillRect(0, 0, size, size)
  const texture = new THREE.CanvasTexture(canvas)
  texture.colorSpace = THREE.SRGBColorSpace
  return texture
}

export const scene: SceneDefinition = {
  maxPixelRatio: 1,
  pauseOffscreen: true,

  setup: ({ THREE, scene, camera, onDispose }) => {
    camera.fov = 32
    camera.near = 0.1
    camera.far = 60
    camera.position.set(0.4, 1.9, 7.2)
    camera.lookAt(0, 0.35, 0)
    camera.updateProjectionMatrix()

    const disposables: { dispose(): void }[] = []
    let disposed = false
    onDispose(() => {
      disposed = true
      for (const thing of disposables) thing.dispose()
    })

    // One warm key from above, and the two bin colours as rims. The signal
    // colours are in the light rather than on the object, so nothing here
    // claims to be an LED.
    const key = new THREE.DirectionalLight(0xffcf8a, 2.6)
    key.position.set(2.2, 4.2, 2.6)
    const household = new THREE.DirectionalLight(0x7bd66b, 0.85)
    household.position.set(-3.4, 0.6, 1.4)
    const recycling = new THREE.DirectionalLight(0x4aa3f0, 0.9)
    recycling.position.set(3.2, 0.2, -2.2)
    scene.add(key, household, recycling, new THREE.AmbientLight(0x39405a, 0.75))

    const spin = new THREE.Group()
    scene.add(spin)

    void (async () => {
      try {
        const [{ GLTFLoader }, { MeshoptDecoder }] = await Promise.all([
          import('three/examples/jsm/loaders/GLTFLoader.js'),
          import('three/examples/jsm/libs/meshopt_decoder.module.js'),
        ])
        const loader = new GLTFLoader()
        loader.setMeshoptDecoder(MeshoptDecoder)
        const gltf = await loader.loadAsync(asset(MODEL))
        if (disposed) return
        const model = gltf.scene

        // Printed matte black, which is what it is: no gloss to invent a
        // finish the object does not have.
        const shell = new THREE.MeshStandardMaterial({
          color: 0x23283a,
          roughness: 0.85,
          metalness: 0.05,
        })
        disposables.push(shell)
        model.traverse((child) => {
          const mesh = child as InstanceType<typeof THREE.Mesh>
          if (!mesh.isMesh) return
          if (mesh.geometry) disposables.push(mesh.geometry)
          mesh.material = shell
        })

        // Measured and fitted rather than scaled by a constant: the mesh is
        // millimetres from a printer, and a magic multiplier would go wrong
        // the next time the enclosure is revised.
        const box = new THREE.Box3().setFromObject(model)
        const size = box.getSize(new THREE.Vector3())
        const mid = box.getCenter(new THREE.Vector3())
        const target = window.innerWidth < 760 ? 2.4 : 3.1
        const fit = target / Math.max(size.x, size.y, size.z, 1e-6)
        model.scale.setScalar(fit)
        model.position.set(-mid.x * fit, -mid.y * fit, -mid.z * fit)
        // Tipped forward a little, the way it sits on a wall.
        model.rotation.x = -0.32
        spin.add(model)
      } catch (error) {
        console.warn('vessel mesh failed to load; the hero is embers alone', error)
      }
    })()

    // ── Embers ────────────────────────────────────────────────────────────
    const texture = emberTexture(THREE)
    disposables.push(texture)
    const count = window.innerWidth < 700 ? EMBERS_MOBILE : EMBERS_DESKTOP
    interface Ember {
      sprite: InstanceType<typeof THREE.Sprite>
      material: InstanceType<typeof THREE.SpriteMaterial>
      speed: number
      drift: number
      phase: number
      life: number
      max: number
      size: number
    }
    const embers: Ember[] = []
    for (let i = 0; i < count; i += 1) {
      const material = new THREE.SpriteMaterial({
        map: texture,
        transparent: true,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        opacity: 0,
      })
      disposables.push(material)
      const sprite = new THREE.Sprite(material)
      scene.add(sprite)
      embers.push({
        sprite, material,
        speed: 0, drift: 0, phase: 0, life: 0, max: 1, size: 0.05,
      })
    }

    function seed(ember: Ember, initial: boolean): void {
      ember.sprite.position.set(
        (Math.random() - 0.5) * 7.5,
        initial ? (Math.random() - 0.5) * 6 : -3.4 - Math.random() * 0.8,
        (Math.random() - 0.5) * 3.5 - 0.5,
      )
      ember.speed = 0.28 + Math.random() * 0.5
      ember.drift = (Math.random() - 0.5) * 0.24
      ember.phase = Math.random() * 10
      ember.life = initial ? Math.random() * 3 : 0
      ember.max = 5.5 + Math.random() * 4.5
      ember.size = 0.028 + Math.random() * 0.055
      ember.sprite.scale.setScalar(ember.size)
    }
    for (const ember of embers) seed(ember, true)

    return (delta, elapsed) => {
      spin.rotation.y += delta * 0.16
      for (const ember of embers) {
        ember.life += delta
        if (ember.life > ember.max) seed(ember, false)
        ember.sprite.position.y += ember.speed * delta
        ember.sprite.position.x += Math.sin(elapsed * 0.7 + ember.phase) * ember.drift * delta
        // Up, brighten, then gutter out: an ember cools as it rises.
        const k = ember.life / ember.max
        ember.material.opacity = Math.min(1, k * 6) * Math.max(0, 1 - k * k) * 0.9
        ember.sprite.scale.setScalar(ember.size * (1 - k * 0.35))
      }
    }
  },
}

export default scene
